import { useState } from 'react';
import { message, Modal } from 'antd';
import { EntityType } from '../../../../types.generated';
import { useEntityRegistry } from '../../../useEntityRegistry';
import { getDeleteEntityMutation, checkEntityAttachedToReadOnlyDatasets } from '../../../shared/deleteUtils';
import analytics, { EventType } from '../../../analytics';
import { useGlossaryEntityData } from '../GlossaryEntityContext';
import { getParentNodeToUpdate, updateGlossarySidebar } from '../../../glossary/utils';
import { useHandleDeleteDomain } from './useHandleDeleteDomain';
import { useIsEntityAttachedToReadOnlyDatasetsQuery } from '../../../../graphql/entity.generated';
import { useTranslation } from '../../../../i18n/useTranslation';

/**
 * Performs the flow for deleting an entity of a given type.
 *
 * @param urn the type of the entity to delete
 * @param type the type of the entity to delete
 * @param name the name of the entity to delete
 */
function useDeleteEntity(
    urn: string,
    type: EntityType,
    entityData: any,
    onDelete?: () => void,
    hideMessage?: boolean,
    skipWait?: boolean,
) {
    const { t } = useTranslation();
    const [hasBeenDeleted, setHasBeenDeleted] = useState(false);
    const entityRegistry = useEntityRegistry();
    const { isInGlossaryContext, urnsToUpdate, setUrnsToUpdate } = useGlossaryEntityData();
    const { handleDeleteDomain } = useHandleDeleteDomain({ entityData, urn });
    const { refetch: checkIsEntityAttachedToReadOnlyDatasets } = useIsEntityAttachedToReadOnlyDatasetsQuery({
        variables: { urn },
        skip: true,
    });

    const maybeDeleteEntity = getDeleteEntityMutation(type)();
    const deleteEntity = (maybeDeleteEntity && maybeDeleteEntity[0]) || undefined;

    async function handleDeleteEntity() {
        const canDelete = await checkEntityAttachedToReadOnlyDatasets({
            entityRegistry,
            entityType: type,
            entityData,
            checkFunction: checkIsEntityAttachedToReadOnlyDatasets,
            modalClassName: 'f-borderless-modal',
            getModalTitle: (name: string) => t('Cannot Delete {{name}}', { name }),
            getModalContent: (name: string) =>
                t('This {{entityName}} is attached to read-only datasets and cannot be deleted.', {
                    entityName: t(name || ''),
                }),
            getModalOkText: () => t('Ok'),
        });

        if (!canDelete) {
            return;
        }

        deleteEntity?.({
            variables: {
                urn,
            },
        })
            .then(() => {
                analytics.event({
                    type: EventType.DeleteEntityEvent,
                    entityUrn: urn,
                    entityType: type,
                });
                if (!hideMessage && !skipWait) {
                    message.loading({
                        content: t('Deleting'),
                        duration: 2,
                    });
                }

                if (entityData.type === EntityType.Domain) {
                    handleDeleteDomain();
                }

                setTimeout(
                    () => {
                        setHasBeenDeleted(true);
                        onDelete?.();
                        if (isInGlossaryContext) {
                            const parentNodeToUpdate = getParentNodeToUpdate(entityData, type);
                            updateGlossarySidebar([parentNodeToUpdate], urnsToUpdate, setUrnsToUpdate);
                        }
                        if (!hideMessage) {
                            message.success({
                                content: t('Deleted {{entityName}}!', {
                                    entityName: entityRegistry.getEntityName(type),
                                }),
                                duration: 2,
                            });
                        }
                    },
                    skipWait ? 0 : 2000,
                );
            })
            .catch((e) => {
                message.destroy();
                message.error({
                    content: t('Failed to delete: {{message}}', { message: e.message || '' }),
                    duration: 3,
                });
            });
    }

    function onDeleteEntity() {
        const displayName =
            (entityData && entityRegistry.getDisplayName(type, entityData)) || entityRegistry.getEntityName(type);
        const entityName = entityRegistry.getEntityName(type);

        Modal.confirm({
            title: t('Delete {{name}}', { name: displayName }),
            content: t('Are you sure you want to delete this {{entityName}}?', {
                entityName: t(entityName || ''),
            }),
            onOk() {
                handleDeleteEntity();
            },
            onCancel() {},
            okText: t('Delete'),
            cancelText: t('Cancel'),
            className: 'f-borderless-modal',
            okButtonProps: {
                type: undefined,
                danger: true,
            },
            autoFocusButton: null,
            maskClosable: true,
            centered: true,
        });
    }

    return { onDeleteEntity, hasBeenDeleted };
}

export default useDeleteEntity;
